import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Minus, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';

interface LeagueSettingsOverlayProps {
  isOpen: boolean;
  onClose: () => void;
  leagueName: string;
}

interface User {
  id: string;
  username: string;
}

const periodLabelKeys: Record<string, string> = {
  today: 'leaderboard.today',
  week: 'leaderboard.week',
  month: 'leaderboard.month',
  season: 'leaderboard.season',
};

const LeagueSettingsOverlay: React.FC<LeagueSettingsOverlayProps> = ({
  isOpen,
  onClose,
  leagueName,
}) => {
  const { t } = useTranslation();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showRemoveUserConfirm, setShowRemoveUserConfirm] = useState<string | null>(null);
  const [leaderboardSettings, setLeaderboardSettings] = useState({
    today: false,
    week: true,
    month: true,
    season: true,
  });

  const users: User[] = [
    { id: '1', username: 'HockeyHero92' },
    { id: '2', username: 'PuckMasterX' },
    { id: '3', username: 'IceQueen77' },
    { id: '4', username: 'GoalieGuru88' },
    { id: '5', username: 'StickWizard45' },
    { id: '6', username: 'FrozenFlame99' },
    { id: '7', username: 'SkateAndScore' },
  ];

  const handleLeaderboardChange = (key: keyof typeof leaderboardSettings) => {
    setLeaderboardSettings(prev => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const handleDeleteGroup = () => {
    setShowDeleteConfirm(false);
    onClose();
  };

  const handleRemoveUser = (_userId: string, _username: string) => {
    setShowRemoveUserConfirm(null);
  };

  if (!isOpen) return null;

  return (
    <div
      data-test-id="league-settings-overlay"
      className="absolute inset-0 z-50 bg-background rounded-2xl overflow-hidden"
    >
      <div className="h-full flex flex-col">
        {/* Header */}
        <header className="bg-background border-b border-border/30 p-4">
          <div className="flex items-center justify-between">
            <button
              data-test-id="back-btn"
              onClick={onClose}
              className="flex items-center gap-2 text-foreground hover:text-muted-foreground transition-smooth"
            >
              <ArrowLeft className="w-5 h-5" />
              <span className="font-medium">{t('actions.back')}</span>
            </button>
            <div className="w-[60px]"></div>
          </div>
        </header>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          <div className="text-center mb-6">
            <h1 className="text-foreground font-bold text-xl mb-2">{t('league.settings.title')}</h1>
            <h2 className="text-foreground font-bold text-lg">{leagueName}</h2>
          </div>

          {/* Show Leaderboards Section */}
          <div className="mb-6">
            <h3 className="text-foreground font-medium text-center mb-4">
              {t('league.settings.showLeaderboards')}
            </h3>
            <div className="flex justify-center gap-6">
              {Object.entries(leaderboardSettings).map(([key, checked]) => (
                <Checkbox
                  key={key}
                  id={key}
                  checked={checked}
                  onCheckedChange={() =>
                    handleLeaderboardChange(key as keyof typeof leaderboardSettings)
                  }
                  label={<span>{t(periodLabelKeys[key] ?? key)}</span>}
                  layout="horizontal"
                />
              ))}
            </div>
          </div>

          {/* Delete Group Button */}
          <div className="flex justify-center mb-6">
            <Button
              data-test-id="delete-league-btn"
              variant="destructive"
              onClick={() => setShowDeleteConfirm(true)}
              className="px-8"
            >
              {t('league.settings.deleteGroup')}
            </Button>
          </div>

          {/* Users List */}
          <div className="mb-4">
            <div className="flex items-center justify-between mb-4">
              <span className="text-foreground font-medium">
                {t('league.settings.usersCount', { count: users.length })}
              </span>
              <span className="text-foreground font-medium">{t('league.settings.remove')}</span>
            </div>

            <div className="space-y-2">
              {users.map(user => (
                <div
                  key={user.id}
                  data-test-id={`league-user-${user.id}`}
                  className="flex items-center justify-between py-3 px-4 bg-surface rounded-lg"
                >
                  <span className="text-foreground font-medium">{user.username}</span>
                  <button
                    data-test-id={`remove-user-btn-${user.id}`}
                    onClick={() => setShowRemoveUserConfirm(user.id)}
                    className="w-8 h-8 bg-red-500 hover:bg-red-600 text-white rounded-full flex items-center justify-center transition-smooth"
                  >
                    <Minus className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Delete Group Confirmation Modal */}
        {showDeleteConfirm && (
          <div className="absolute inset-0 bg-black/50 flex items-center justify-center p-4 z-60">
            <div
              data-test-id="delete-league-modal"
              className="bg-background rounded-xl p-6 w-full max-w-sm"
            >
              <div className="flex justify-end mb-4">
                <button
                  data-test-id="close-delete-modal-btn"
                  onClick={() => setShowDeleteConfirm(false)}
                  className="text-muted-foreground hover:text-foreground"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="text-center mb-6">
                <h3 className="text-foreground font-bold text-lg mb-3">
                  {t('league.settings.deleteConfirmNamed', { name: leagueName })}
                </h3>
                <p className="text-muted-foreground">{t('league.settings.deleteMessage')}</p>
              </div>

              <div className="space-y-3">
                <Button
                  data-test-id="confirm-delete-league-btn"
                  variant="destructive"
                  onClick={handleDeleteGroup}
                  className="w-full"
                >
                  {t('league.settings.delete')}
                </Button>
                <Button
                  data-test-id="cancel-delete-league-btn"
                  variant="secondary"
                  onClick={() => setShowDeleteConfirm(false)}
                  className="w-full"
                >
                  {t('league.settings.cancel')}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Remove User Confirmation Modal */}
        {showRemoveUserConfirm && (
          <div className="absolute inset-0 bg-black/50 flex items-center justify-center p-4 z-60">
            <div
              data-test-id="remove-user-modal"
              className="bg-background rounded-xl p-6 w-full max-w-sm"
            >
              <div className="flex justify-end mb-4">
                <button
                  data-test-id="close-remove-user-modal-btn"
                  onClick={() => setShowRemoveUserConfirm(null)}
                  className="text-muted-foreground hover:text-foreground"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="text-center mb-6">
                <h3 className="text-foreground font-bold text-lg mb-3">
                  {t('league.settings.removePlayerConfirmNamed', {
                    name: users.find(u => u.id === showRemoveUserConfirm)?.username,
                  })}
                </h3>
                <p className="text-muted-foreground">{t('league.settings.deleteMessage')}</p>
              </div>

              <div className="space-y-3">
                <Button
                  data-test-id="confirm-remove-user-btn"
                  variant="default"
                  onClick={() =>
                    handleRemoveUser(
                      showRemoveUserConfirm,
                      users.find(u => u.id === showRemoveUserConfirm)?.username || ''
                    )
                  }
                  className="w-full"
                >
                  {t('league.settings.removePlayer')}
                </Button>
                <Button
                  data-test-id="cancel-remove-user-btn"
                  variant="secondary"
                  onClick={() => setShowRemoveUserConfirm(null)}
                  className="w-full"
                >
                  {t('league.settings.cancel')}
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default LeagueSettingsOverlay;
